/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, Flex } from '@chakra-ui/react';
import { useForm, useFieldArray } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useParams, useLocation, useBlocker } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import useCustomToast from '@/components/common/Toast';
import CustomButton from '@/components/common/Button';
import CategorySelectionSection from '@/components/AddAddons/CategorySelectionSection';
import AddonsDetailsSection from '@/components/AddAddons/AddonsDetailsSection';
import VariantDetailsSection from '@/components/AddAddons/AddonsVariantDetailsSection';
import { useQueryClient } from 'react-query';
import { useStore } from '@/store/store';
import { useCreateAddOn, useUpdateAddOn } from '@/services/addAddons';
import { useGetAddOnDetail } from '@/services/manageAddons';
import { AddOnFormData } from '@/services/addAddons/types';
import CustomConfirmationModal from '@/components/common/ConfirmationModal';
import { WarningIcon } from '@chakra-ui/icons';

const addonsValidationSchema = yup.object({
  categorySelection: yup
    .object({
      category: yup.string().required('Category is required'),
      subCategory: yup.string().optional(),
    })
    .required(),
  productDetails: yup
    .object({
      name: yup.string().required('Add-on name is required'),
      discount: yup
        .number()
        .typeError('Must be a valid number')
        .min(0)
        .max(100)
        .optional()
        .transform((v, o) => (o === '' ? undefined : v)),
      shortDescription: yup.string().required('Description is required'),
    })
    .required(),
  variants: yup
    .array()
    .of(
      yup.object({
        color: yup
          .object({
            name: yup.string().required('Color name is required'),
            hex_code: yup.string().optional(),
            id: yup.number().optional(),
          })
          .required(),
        isAvailable: yup.boolean().required(),
        productImage: yup
          .array()
          .of(yup.object().shape({}))
          .min(2, 'At least 2 images are required per variant')
          .required('Images are required'),
        sizes: yup
          .array()
          .of(
            yup.object({
              id: yup.number().optional(),
              size: yup.string().required('Size is required'),
              variantPrice: yup
                .number()
                .required('Price is required')
                .min(0.01, 'Price must be greater than 0')
                .typeError('Price must be a valid number'),
              stockQuantity: yup
                .number()
                .required('Stock quantity is required')
                .min(0, 'Stock cannot be negative')
                .integer('Stock must be a whole number')
                .typeError('Stock must be a valid number'),
              weight: yup
                .string()
                .required('Weight is required')
                .matches(/^\d*\.?\d+$/, 'Weight must be a valid number')
                .test('weight-positive', 'Weight must be greater than 0', (value) => {
                  if (!value) return false;
                  const num = parseFloat(value);
                  return num > 0;
                }),
              isPrimary: yup.boolean().optional(),
              sku: yup.string().optional(),
            }),
          )
          .min(1, 'At least one size per variant is required'),
      }),
    )
    .min(1, 'At least one variant is required'),
}) as yup.ObjectSchema<AddOnFormData>;

const AddAddons = () => {
  const navigate = useNavigate();
  const { addonId: id } = useParams();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { showToast } = useCustomToast();
  const { data: addon, refetch } = useGetAddOnDetail(id);
  const { mutateAsync: createAddOn } = useCreateAddOn();
  const { mutateAsync: updateAddOn } = useUpdateAddOn();
  const { user } = useStore();
  const vendorCurrency = user?.currency || 'NPR';
  const fromTab = location.state?.fromTab || 0;

  const [renderKey, setRenderKey] = useState(0);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const [isIntentionalLeave, setIsIntentionalLeave] = useState(false);
  const [isLeavingConfirmed, setIsLeavingConfirmed] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [, setNextLocation] = useState<string | null>(null);
  const blockHandledRef = useRef(false);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<AddOnFormData>({
    resolver: yupResolver(addonsValidationSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      categorySelection: { category: '', subCategory: '' },
      productDetails: {
        name: '',
        discount: undefined,
        shortDescription: '',
      },
      variants: [
        {
          color: { name: '', hex_code: '', id: 0 },
          displayName: '',
          isAvailable: false,
          productImage: [],
          sizes: [
            {
              id: 0,
              size: '',
              variantPrice: 0,
              stockQuantity: 0,
              weight: '0.00',
              isPrimary: false,
              sku: '',
            },
          ],
        },
      ],
    },
  });

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'variants',
  });

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (isSubmittingForm) return false;
    if (isLeavingConfirmed) return false;
    if (isIntentionalLeave) return false;

    return isFormDirty && currentLocation.pathname !== nextLocation.pathname;
  });

  useEffect(() => {
    const element = document.querySelector('.chakra-button.css-trj4gu') as HTMLElement;
    if (element) element.style.display = 'none';
  }, []);

  const [hasInitializedEditData, setHasInitializedEditData] = useState(false);

  useEffect(() => {
    if (blocker.state === 'blocked' && !blockHandledRef.current) {
      blockHandledRef.current = true;
      setShowLeaveModal(true);
      setNextLocation(blocker.location?.pathname + (blocker.location?.search || ''));
    }

    if (blocker.state === 'unblocked') {
      blockHandledRef.current = false;
      setShowLeaveModal(false);
      setNextLocation(null);
    }
  }, [blocker.state, blocker.location]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isFormDirty && !isSubmittingForm && !isIntentionalLeave) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isFormDirty, isSubmittingForm, isIntentionalLeave]);

  useEffect(() => {
    if (!id || !addon || hasInitializedEditData) return;

    const variantMap = new Map<string, AddOnFormData['variants'][number]>();

    (addon.product_variants || []).forEach((apiVariant: any) => {
      const colorKey = `${apiVariant.color || ''}_${apiVariant.value || ''}`;
      const colorId = Number(apiVariant.color_id || apiVariant.color?.id || 0);
      const variantId = Number(apiVariant.id);

      if (!variantMap.has(colorKey)) {
        variantMap.set(colorKey, {
          color: {
            id: colorId,
            name: apiVariant.color || '',
            hex_code: apiVariant.value || '',
          },
          displayName: '',
          isAvailable: apiVariant.is_available ?? true,
          productImage: Array(4)
            .fill(null)
            .map(() => ({ id: 0, url: '', file: undefined })),
          sizes: [],
          variantId: variantId,
        });
      }

      const entry = variantMap.get(colorKey)!;

      if (entry.sizes.length === 0) {
        (apiVariant.images || []).slice(0, 4).forEach((img: any, i: number) => {
          entry.productImage[i] = {
            id: Number(img.id) || 0,
            url: img.image || '',
            file: undefined,
          };
        });
      }

      entry.sizes.push({
        id: Number(apiVariant.id) || 0,
        size: String(apiVariant.size?.id || ''),
        variantPrice: Number(apiVariant.variant_price) || 0,
        stockQuantity: Number(apiVariant.stock_quantity) || 0,
        weight: String(apiVariant.variant_weight || '0.00'),
        isPrimary: Boolean(apiVariant.is_primary),
        sku: apiVariant.sku || '',
      });
    });

    const formVariants = Array.from(variantMap.values());

    reset(
      {
        categorySelection: {
          category: String(addon.category?.id || addon.category || addon.sub_category?.id || ''),
        },
        productDetails: {
          name: (addon.name || '').trim(),
          discount: addon.discount_percent ? Number(addon.discount_percent) : undefined,
          shortDescription: (addon.description || '').trim(),
        },
        variants:
          formVariants.length > 0
            ? formVariants
            : [
                {
                  color: { name: '', hex_code: '', id: 0 },
                  displayName: '',
                  isAvailable: false,
                  productImage: [],
                  sizes: [
                    {
                      id: 0,
                      size: '',
                      variantPrice: 0,
                      stockQuantity: 0,
                      weight: '0.00',
                      isPrimary: false,
                      sku: '',
                    },
                  ],
                },
              ],
      },
      { keepDefaultValues: false, keepErrors: false, keepDirty: false },
    );

    setHasInitializedEditData(true);
  }, [id, addon, hasInitializedEditData, reset]);

  useEffect(() => {
    const subscription = watch(() => {
      setIsFormDirty(true);
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    return () => {
      setIsIntentionalLeave(false);
      setIsLeavingConfirmed(false);
      setIsFormDirty(false);
    };
  }, []);

  const handleReset = () => {
    reset({
      categorySelection: { category: '', subCategory: '' },
      productDetails: {
        name: '',
        discount: undefined,
        shortDescription: '',
      },
      variants: [
        {
          color: { name: '', hex_code: '', id: 0 },
          displayName: '',
          isAvailable: false,
          productImage: [],
          sizes: [
            {
              id: 0,
              size: '',
              variantPrice: 0,
              stockQuantity: 0,
              weight: '0.00',
              isPrimary: false,
              sku: '',
            },
          ],
        },
      ],
    });

    setRenderKey((prev) => prev + 1);
    setIsFormDirty(false);
    setIsLeavingConfirmed(false);
    setIsIntentionalLeave(true);

    setTimeout(() => {
      navigate('/manage-addons', { state: { tabIndex: fromTab || 0 } });
    }, 0);
  };

  const onSubmit = async (data: AddOnFormData) => {
    setIsSubmittingForm(true);

    if (!data.productDetails) {
      showToast('error', 'Add-on details are missing.');
      return;
    }

    const formData = new FormData();
    const isEdit = !!id;

    formData.append('name', data.productDetails.name.trim());
    formData.append('category_id', data.categorySelection.category || '');
    formData.append('description', data.productDetails.shortDescription.trim());
    formData.append('discount_percent', String(data.productDetails.discount ?? 0));
    formData.append('is_active', 'true');
    formData.append('is_add_on', 'true');

    const processedVariants = new Map<string, any>();

    data.variants.forEach((variant) => {
      if (!variant.color?.name || !variant.color?.hex_code) return;
      const colorKey = `${variant.color.name}_${variant.color.hex_code}`;
      if (!processedVariants.has(colorKey)) {
        processedVariants.set(colorKey, {
          color: variant.color,
          sizes: [...variant.sizes],
          images: [...variant.productImage],
        });
      } else {
        processedVariants.get(colorKey).sizes.push(...variant.sizes);
      }
    });

    let colorIndex = 0;
    for (const [, variantInfo] of processedVariants.entries()) {
      const { color, sizes, images } = variantInfo;

      formData.append(`product_variant[${colorIndex}][color]`, color.name);
      formData.append(`product_variant[${colorIndex}][value]`, color.hex_code);

      if (isEdit && color.id && color.id !== 0) {
        formData.append(`product_variant[${colorIndex}][color_id]`, String(color.id));
      }

      sizes.forEach((size: any, sIdx: number) => {
        if (size.id > 0) {
          formData.append(`product_variant[${colorIndex}][variants][${sIdx}][id]`, String(size.id));
        }
        formData.append(
          `product_variant[${colorIndex}][variants][${sIdx}][size_id]`,
          String(size.size),
        );
        formData.append(
          `product_variant[${colorIndex}][variants][${sIdx}][variant_price]`,
          String(size.variantPrice),
        );
        formData.append(
          `product_variant[${colorIndex}][variants][${sIdx}][variant_weight]`,
          String(size.weight || '0.00'),
        );
        formData.append(
          `product_variant[${colorIndex}][variants][${sIdx}][stock_quantity]`,
          String(size.stockQuantity),
        );
        formData.append(`product_variant[${colorIndex}][variants][${sIdx}][sku]`, size.sku ?? '');
        formData.append(`product_variant[${colorIndex}][variants][${sIdx}][is_available]`, 'true');
        formData.append(`product_variant[${colorIndex}][variants][${sIdx}][is_active]`, 'true');
        formData.append(
          `product_variant[${colorIndex}][variants][${sIdx}][is_primary]`,
          String(size.isPrimary ?? false),
        );

        if (sIdx === 0) {
          let imgIdx = 0;
          images.forEach((img: any) => {
            if (!img || (!img.file && !img.url && img.id === 0)) return;

            const isNewUpload = img.file instanceof File;

            if (isNewUpload) {
              const safeName = `vimg-${colorIndex}-${sIdx}-${imgIdx}-${Date.now()}.jpg`;
              const safeFile = new File([img.file], safeName, {
                type: img.file.type || 'image/jpeg',
              });

              formData.append(
                `product_variant[${colorIndex}][variants][${sIdx}][images][${imgIdx}][image]`,
                safeFile,
              );
              formData.append(
                `product_variant[${colorIndex}][variants][${sIdx}][images][${imgIdx}][is_primary]`,
                imgIdx === 0 ? 'true' : 'false',
              );

              if (isEdit && img.id && img.id > 0) {
                formData.append(
                  `product_variant[${colorIndex}][variants][${sIdx}][images][${imgIdx}][id]`,
                  String(img.id),
                );
              }
              imgIdx++;
            }
          });
        }
      });
      colorIndex++;
    }

    try {
      if (isEdit) {
        await updateAddOn({ id: id!, payload: formData });
        showToast('success', 'Add-on updated successfully');
        queryClient.invalidateQueries({ queryKey: ['addon', id] });
        await refetch();
      } else {
        await createAddOn(formData);
        showToast('success', 'Add-on created successfully');
      }

      queryClient.invalidateQueries({ queryKey: ['addons'] });
      queryClient.invalidateQueries({ queryKey: ['addOns'] });

      setIsFormDirty(false);
      navigate('/manage-addons', { state: { tabIndex: isEdit ? fromTab : 2 } });
    } catch (error: any) {
      console.error('API Error:', error);

      if (error?.response?.status === 400 && error?.response?.data) {
        const responseData = error.response.data;
        const fieldErrors: string[] = [];
        Object.entries(responseData).forEach(([messages]) => {
          if (Array.isArray(messages)) {
            messages.forEach((msg) => fieldErrors.push(msg as string));
          } else if (typeof messages === 'string') {
            fieldErrors.push(messages);
          }
        });
        if (fieldErrors.length > 0) {
          showToast('error', fieldErrors.join('\n'));
          return;
        }
        const fallbackMsg =
          responseData?.detail ||
          responseData?.non_field_errors?.[0] ||
          responseData?.message ||
          'Failed to save add-on. Please check your input.';
        showToast('error', fallbackMsg);
      } else if (error?.response?.status === 500) {
        showToast('error', 'Something went wrong on the server. Please try again later.');
      } else {
        showToast('error', error?.message || 'Failed to save add-on');
      }
    } finally {
      setIsSubmittingForm(false);
    }
  };

  return (
    <Box id="add-product-box" fontFamily={'primary'} key={renderKey} p={{ base: 4, md: 6 }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Flex direction="column" gap={5}>
          <CategorySelectionSection control={control} errors={errors} id={id} />
          <AddonsDetailsSection control={control} errors={errors} />
          <VariantDetailsSection
            control={control}
            errors={errors}
            fields={fields}
            append={append}
            remove={remove}
            update={update}
            watch={watch}
            setValue={setValue}
            id={id}
            showToast={showToast}
            vendorCurrency={vendorCurrency}
          />
          <Flex gap={2} justify={'space-between'}>
            <CustomButton onClick={handleReset} border={'1px solid'} variant="outline">
              Cancel
            </CustomButton>
            <CustomButton isLoading={isSubmitting} type="submit" mr={{ base: 16, lg: 36 }}>
              Submit
            </CustomButton>
          </Flex>
        </Flex>
      </form>

      <CustomConfirmationModal
        isOpen={showLeaveModal}
        onClose={() => {
          blockHandledRef.current = false;
          setShowLeaveModal(false);
          setNextLocation(null);
          setIsLeavingConfirmed(false);

          if (blocker.state === 'blocked') {
            blocker.reset?.();
          }
        }}
        onConfirm={() => {
          blockHandledRef.current = false;
          setShowLeaveModal(false);
          setIsLeavingConfirmed(true);

          if (blocker.state === 'blocked') {
            blocker.proceed?.();

            setTimeout(() => {
              setIsFormDirty(false);
              setNextLocation(null);
            }, 0);
          }
        }}
        icon={WarningIcon}
        title="Unsaved Changes"
        description={
          <>
            You have unsaved changes.
            <br />
            Leaving this page will discard them.
          </>
        }
        buttonText="Leave Anyway"
      />
    </Box>
  );
};

export default AddAddons;
